import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useStream } from "@langchain/langgraph-sdk/react";
import { LoadExternalComponent } from "@langchain/langgraph-sdk/react-ui";
import { Client } from "@langchain/langgraph-sdk";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import LogoBars from "./LogoBars";
import { useOrganization } from '../../../context/OrganizationContext';
import { useUser } from '../../../context/UserContext';
import { CalendarCheck, Clock, Bot } from "lucide-react";
import FloatingChatInput from "./FloatingChatInput";

import { uiMessageReducer } from "@langchain/langgraph-sdk/react-ui";
import apisConfig from '../../../config/apis.json';

// Enhanced LoadExternalComponent wrapper that waits for DOM attachment
function SafeLoadExternalComponent({ stream, message, fallback, ...props }) {
  const hostRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let raf = 0;
    const waitUntilConnected = () => {
      if (host.isConnected) {
        setIsConnected(true);
      } else {
        raf = requestAnimationFrame(waitUntilConnected);
      }
    };
    waitUntilConnected();

    return () => cancelAnimationFrame(raf);
  }, []);

  if (!isConnected) {
    return (
      <div ref={hostRef} className="component-host" style={{ width: "100%", height: "100%" }}>
        {fallback}
      </div>
    );
  }

  return (
    <div ref={hostRef} className="component-host" style={{ width: "100%", height: "100%" }}>
      <LoadExternalComponent
        stream={stream}
        message={message}
        fallback={fallback}
        {...props}
      />
    </div>
  );
}

// Design system components from the provided design
const Avatar = ({ children, className = "" }) => (
  <div className={`relative inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-[10px] font-medium text-gray-600 ${className}`}>{children}</div>
);

const AvatarImage = ({ src, alt }) => (
  <img src={src} alt={alt} className="h-full w-full object-cover" onError={(e)=>{ e.currentTarget.style.display='none'; }} />
);
const AvatarFallback = ({ children }) => <span className="absolute">{children}</span>;

// Scheduled Run Header Component
function ScheduledRunHeader({ thread }) {
  const executionTime = thread?.executed_at || thread?.started_at || new Date();
  const isOvernight = new Date(executionTime).getHours() < 6 || new Date(executionTime).getHours() > 22;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="border-b border-border bg-gradient-to-r from-brand-blue-light/10 via-brand-pink/5 to-brand-blue-light/15 backdrop-blur-sm"
    >
      <div className="mx-auto max-w-6xl px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-pink/10">
              <CalendarCheck className="h-4 w-4 text-brand-pink" />
            </div>
            
            <div className="flex-1 min-w-0 space-y-1">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                Ran automatically{isOvernight ? ' overnight' : ''}
              </span>
              <p 
                className="text-sm text-foreground truncate cursor-help" 
                title={thread?.prompt || 'GA4 Analysis'}
              >
                {(thread?.prompt && thread.prompt.length > 80) 
                  ? `${thread.prompt.substring(0, 80)}...` 
                  : (thread?.prompt || 'GA4 Analysis')
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-xs text-grey shrink-0">
            <Clock className="h-3.5 w-3.5" />
            <span className="font-medium">
              {new Date(executionTime).toLocaleString([], {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Helper function to detect if content is code
function isCodeContent(content) {
  if (typeof content !== 'string') return false;
  
  // Check for common code patterns
  const codePatterns = [
    /^import\s+\w+/m,                    // import statements
    /^from\s+\w+\s+import/m,             // from...import statements
    /^\s*def\s+\w+\(/m,                  // function definitions
    /^\s*class\s+\w+/m,                  // class definitions
    /^\s*#\s*.+$/m,                      // comments
    /\bprint\s*\(/,                      // print statements
    /\bdf\s*\[/,                         // pandas dataframe operations
    /\.groupby\(/,                       // pandas groupby
    /\.to_csv\(/,                        // CSV operations
    /\bpd\./,                            // pandas prefix
    /^\s*\w+\s*=\s*.+\.groupby/m,        // assignment with groupby
    /^\s*\w+\s*=\s*.+\.to_csv/m,         // assignment with to_csv
    /aggregated_df|df\.|pd\.|numpy|np\./  // data science libraries
  ];
  
  return codePatterns.some(pattern => pattern.test(content));
}

// Detect programming language from content
function detectLanguage(content) {
  if (typeof content !== 'string') return 'text';
  
  // Python patterns
  if (
    /\bimport\s+\w+|from\s+\w+\s+import|\bdef\s+\w+\(|\bprint\s*\(|\bpd\.|\.to_csv\(/.test(content)
  ) {
    return 'python';
  }
  
  // JavaScript patterns
  if (
    /\bconst\s+\w+|\blet\s+\w+|\bvar\s+\w+|\bconsole\.log\(|\bfunction\s+\w+\(/.test(content)
  ) {
    return 'javascript';
  }
  
  // SQL patterns
  if (
    /\bSELECT\s+|\bFROM\s+|\bWHERE\s+|\bINSERT\s+|\bUPDATE\s+|\bDELETE\s+/i.test(content)
  ) {
    return 'sql';
  }
  
  return 'text';
}

// Console-like code renderer component
function ConsoleCodeRenderer({ content, language = 'python' }) {
  // Get console theme based on language
  const getConsoleTheme = (lang) => {
    switch (lang) {
      case 'python':
        return {
          header: 'Python',
          bg: 'bg-gray-900 dark:bg-black',
          text: 'text-green-400',
          headerBg: 'bg-gray-800 dark:bg-gray-900'
        };
      case 'javascript':
        return {
          header: 'JavaScript',
          bg: 'bg-gray-900 dark:bg-black',
          text: 'text-yellow-400',
          headerBg: 'bg-gray-800 dark:bg-gray-900'
        };
      case 'sql':
        return {
          header: 'SQL',
          bg: 'bg-blue-900 dark:bg-blue-950',
          text: 'text-blue-200',
          headerBg: 'bg-blue-800 dark:bg-blue-900'
        };
      default:
        return {
          header: 'Code',
          bg: 'bg-gray-900 dark:bg-black',
          text: 'text-gray-300',
          headerBg: 'bg-gray-800 dark:bg-gray-900'
        };
    }
  };

  const theme = getConsoleTheme(language);

  return (
    <div className="my-3 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Minimal Console Header */}
      <div className={`px-3 py-1.5 ${theme.headerBg} border-b border-gray-600`}>
        <span className="text-xs font-mono text-gray-400">
          {theme.header}
        </span>
      </div>
      
      {/* Console Body */}
      <div className={`${theme.bg} ${theme.text} p-3 font-mono text-sm overflow-x-auto`}>
        <pre className="whitespace-pre-wrap leading-relaxed">
          <code className={`language-${language}`}>
            {content}
          </code>
        </pre>
      </div>
    </div>
  );
}

// Enhanced content renderer that chooses between console and markdown
function SmartContentRenderer({ content }) {
  const [parsedContent, setParsedContent] = React.useState(null);
  const [isCodeBlock, setIsCodeBlock] = React.useState(false);
  
  React.useEffect(() => {
    if (!content) {
      setParsedContent(null);
      setIsCodeBlock(false);
      return;
    }

    // If content is an object with a 'code' property - EXPLICIT code block
    if (typeof content === 'object' && content?.code) {
      setParsedContent(content.code);
      setIsCodeBlock(true);
      return;
    }
    
    // If content is a string
    if (typeof content === 'string') {
      // Try to parse as complete JSON - check for EXPLICIT {"code": "..."} structure
      try {
        const parsed = JSON.parse(content);
        if (parsed && typeof parsed === 'object' && parsed.code) {
          setParsedContent(parsed.code);
          setIsCodeBlock(true);
          return;
        }
      } catch {
        // JSON parsing failed - check if it's a streaming {"code":"..." structure
        // Only match if it explicitly starts with {"code" pattern
        if (content.trim().startsWith('{') && content.includes('"code"')) {
          const codeMatch = content.match(/\{\s*["']code["']\s*:\s*["']([^]*?)(?:["']\s*\})?$/s);
          if (codeMatch && codeMatch[1]) {
            // Unescape the content
            let codeContent = codeMatch[1];
            // Handle escaped characters from JSON
            codeContent = codeContent
              .replace(/\\n/g, '\n')
              .replace(/\\t/g, '\t')
              .replace(/\\"/g, '"')
              .replace(/\\\\/g, '\\');
            
            setParsedContent(codeContent);
            setIsCodeBlock(true);
            return;
          }
        }
      }
      
      // Everything else is markdown - let remarkGfm handle it
      setParsedContent(content);
      setIsCodeBlock(false);
    }
  }, [content]);
  
  // Render based on buffered state
  if (!parsedContent && !content) return null;
  
  if (isCodeBlock && parsedContent) {
    const detectedLang = detectLanguage(parsedContent);
    return <ConsoleCodeRenderer content={parsedContent} language={detectedLang} />;
  }
  
  return <MarkdownBody content={parsedContent || content} />;
}

// Helper function to group consecutive messages by type (human/ai)
function groupMessages(messages) {
  const out = [];
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    const prev = out[out.length - 1];
    if (prev && prev.type === m.type) prev.items.push(m); else out.push({ type: m.type, items: [m] });
  }
  return out;
}

function MessageGroup({ children, side = "left", type }) {
  const isRight = side === "right";
  const ai = type === "ai";
  // Wider width for AI; slightly narrower for human
  const widthClass = ai ? "max-w-[95%] md:max-w-[80rem]" : "max-w-[80%] md:max-w-[50rem]";
  return (
    <div className={`flex w-full ${isRight ? "justify-end" : "justify-start"}`}>
      <div className={`flex ${widthClass} items-start gap-2 ${isRight ? "flex-row-reverse" : "flex-row"}`}>
        <Avatar className="h-8 w-8 shadow-sm">
          {ai ? (
            <LogoBars />
          ) : (
            <AvatarImage alt="You" src="" />
          )}
          <AvatarFallback>{ai ? "" : "You"}</AvatarFallback>
        </Avatar>
        <div className={`flex w-full flex-col gap-1.5 ${isRight ? "items-end" : "items-start"}`}>{children}</div>
      </div>
    </div>
  );
}

function MessageBubble({ children, type }) {
  const isHuman = type === "human";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.18 }}
      className={`relative w-full rounded-xl px-3 py-2 shadow-sm ${
        isHuman ? "bg-[#D3E3FD]/40 text-gray-900 dark:bg-[#D3E3FD]/15 dark:text-gray-100" : "bg-white/80 backdrop-blur-sm text-gray-900 ring-1 ring-gray-200 dark:bg-gray-800/80 dark:text-gray-100 dark:ring-gray-700"
      }`}
    >
      <div className="prose prose-sm max-w-none dark:prose-invert prose-pre:rounded-xl prose-pre:shadow-sm prose-ol:list-decimal prose-ol:pl-6 prose-li:pl-1">{children}</div>
    </motion.div>
  );
}

// ChatGPT-like vertical rail with dots (no numbers). Last step looks active.
function AiVerticalStepper({ items, thread, currentNode, isLoading }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.18 }}
      className="w-full rounded-xl border border-gray-200 bg-white/80 backdrop-blur-sm shadow-lg dark:border-gray-600 dark:bg-gray-800/80"
    >
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-600 px-3 py-1.5">
        <div className="text-xs font-medium text-gray-700 dark:text-gray-300">Agent </div>
        {/* <div className="text-xs text-gray-500 dark:text-gray-400">{items.length > 0 ? `${items.length} steps` : 'Starting...'}</div> */}
      </div>
      <ol className="relative ml-5 py-2">
        <span className="absolute left-0 top-0 h-full w-px bg-gray-300 dark:bg-gray-600" />
        {items.map((m, idx) => {
          const isLastStep = idx === items.length - 1;
          const isActive = isLastStep && isLoading && currentNode;
          
          return (
            <li key={m.id} className="relative pl-5 pr-3 py-2">
              <span
                className={`absolute left-[-5px] top-3 inline-flex rounded-full ${
                  isActive ? 'h-2.5 w-2.5 bg-brand-pink ring-4 ring-brand-pink/10' : 
                  isLastStep ? 'h-2.5 w-2.5 bg-gray-600 ring-4 ring-gray-200 dark:bg-gray-400 dark:ring-gray-600' : 'h-2 w-2 bg-gray-400 dark:bg-gray-500'
                }`}
              />
              <div className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">
                {new Date(m.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="prose prose-sm max-w-none dark:prose-invert prose-ol:list-decimal prose-ol:pl-6 prose-li:pl-1">
                <SmartContentRenderer content={m.content} />
              </div>
              
              {/* Render UI Components associated with this message */}
              {thread?.values?.ui
                ?.filter((ui) => ui.metadata?.message_id === m.id)
                .map((ui) => (
                  <div 
                    key={ui.id} 
                    className="mt-3 w-full"
                  >
                    <SafeLoadExternalComponent
                      stream={thread}
                      message={ui}
                      fallback={
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-gray-600">Loading GA4 component...</span>
                          </div>
                        </div>
                      }
                    />
                  </div>
                ))}
              
              {/* Show typing indicator below the content and UI components if this is the active step */}
              {isActive && (
                <div className="flex items-center gap-2 py-2 mt-2 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex gap-1">
                    <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-gray-600 dark:bg-gray-400 [animation-delay:0ms]" />
                    <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-gray-600 dark:bg-gray-400 [animation-delay:100ms]" />
                    <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-gray-600 dark:bg-gray-400 [animation-delay:200ms]" />
                  </div>
                  <span className="text-xs text-gray-600 dark:text-gray-300">
                    {/* Processing: {currentNode.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} */}
                    Working .. 
                  </span>
                </div>
              )}
            </li>
          );
        })}
        
        {/* Show a loading step if we have no items yet but are currently processing */}
        {items.length === 0 && isLoading && currentNode && (
          <li className="relative pl-5 pr-3 py-2">
            <span className="absolute left-[-5px] top-3 inline-flex h-2.5 w-2.5 rounded-full bg-brand-pink ring-4 ring-brand-pink/10" />
            <div className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className="flex items-center gap-2 py-1">
              <div className="flex gap-1">
                <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-gray-600 dark:bg-gray-400 [animation-delay:0ms]" />
                <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-gray-600 dark:bg-gray-400 [animation-delay:100ms]" />
                <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-gray-600 dark:bg-gray-400 [animation-delay:200ms]" />
              </div>
              <span className="text-xs text-gray-600 dark:text-gray-300">
                {/* Processing: {currentNode.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} */}
                Thinking ..
              </span>
            </div>
          </li>
        )}
      </ol>
    </motion.div>
  );
}

function TimeStamp({ value, align = "left" }) {
  const time = React.useMemo(() => {
    try {
      const d = value ? new Date(value) : new Date();
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  }, [value]);
  return <div className={`mt-1 text-[11px] ${align === "right" ? "text-brand-pink/70" : "text-gray-500 dark:text-gray-400"}`}>{time}</div>;
}

function TypingIndicator({ text = "Analyzing GA4 data..." }) {
  return (
    <div className="flex justify-start">
      <div className="rounded-xl border border-gray-200 bg-white/80 backdrop-blur-sm p-2.5 pr-3 shadow-sm dark:border-gray-600 dark:bg-gray-800/80">
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-gray-600 dark:bg-gray-400 [animation-delay:0ms]" />
            <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-gray-600 dark:bg-gray-400 [animation-delay:100ms]" />
            <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-gray-600 dark:bg-gray-400 [animation-delay:200ms]" />
          </div>
          <span className="text-xs text-gray-600 dark:text-gray-300">{text}</span>
        </div>
      </div>
    </div>
  );
}

function MarkdownBody({ content }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const blocks = document.querySelectorAll("pre code");
    blocks.forEach((block) => {
      const pre = block.closest("pre");
      if (!pre) return;
      if (pre.querySelector("button[data-copy]")) return;
      const btn = document.createElement("button");
      btn.setAttribute("data-copy", "true");
      btn.className = "absolute right-2 top-2 inline-flex items-center gap-1 rounded-md border bg-white px-2 py-1 text-xs shadow-sm";
      btn.innerHTML = `${copied ? "✓" : ""} Copy`;
      btn.onclick = async () => {
        try {
          await navigator.clipboard.writeText(block.textContent || "");
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {}
      };
      pre.style.position = "relative";
      pre.appendChild(btn);
    });
  }, [content, copied]);

  const value = typeof content === "string" ? content : JSON.stringify(content, null, 2);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeHighlight]}
      components={{
        a: ({ node, ...props }) => (
          <a className="underline decoration-dotted underline-offset-2 hover:text-blue-600" {...props} />
        ),
        code: ({ inline, className, children, ...props }) => inline ? (
          <code className="rounded bg-gray-100 px-1 py-0.5 text-[0.85em]" {...props}>{children}</code>
        ) : (
          <pre className="my-2 overflow-auto rounded-lg border bg-gray-50 p-2 text-sm"><code className={className} {...props}>{children}</code></pre>
        ),
        table: ({children}) => (
          <div className="not-prose my-2 overflow-hidden rounded-lg border">
            <table className="w-full text-sm">{children}</table>
          </div>
        ),
        th: ({children}) => (
          <th className="bg-gray-100 px-2 py-1 text-left font-medium">{children}</th>
        ),
        td: ({children}) => (
          <td className="px-2 py-1 text-gray-600">{children}</td>
        ),
        ol: ({ children, start, ...props }) => (
          <ol 
            className="list-decimal list-outside pl-6 space-y-1 my-2" 
            start={start}
            {...props}
          >
            {children}
          </ol>
        ),
        ul: ({ children, ...props }) => (
          <ul className="list-disc list-outside pl-6 space-y-1 my-2" {...props}>
            {children}
          </ul>
        ),
        li: ({ children, ...props }) => (
          <li className="pl-1" {...props}>
            {children}
          </li>
        ),
      }}
    >
      {value}
    </ReactMarkdown>
  );
}

const GA4Agent = ({ thread, shouldRunAgent, onAgentRun }) => {
  const [currentNode, setCurrentNode] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const { organizationData } = useOrganization();
  const { orgId } = useOrganization();
  const { userInfo } = useUser();

  // Get thread_id - use thread_id (UUID) from server for LangGraph
  const getThreadId = () => {
    // thread_id is a UUID returned from server, used for LangGraph SDK
    return thread?.thread_id || null;
  };

  const [threadId, setThreadId] = useState(getThreadId());
  const [activeRunId, setActiveRunId] = useState(null); // Store run_id to rejoin stream

  // Reset component state when thread changes
  useEffect(() => {
    const newThreadId = getThreadId();
    if (newThreadId !== threadId) {
      setThreadId(newThreadId);
      setCurrentNode(null);
      eventLogRef.current = [];
      setIsRunning(false);
      setIsInitializing(false);
      setError(null);
      setReconnectAttempts(0);
      setIsReconnecting(false);
      
      // Check if this thread has an active run_id stored
      const storedRunId = sessionStorage.getItem(`ga4_run_${newThreadId}`);
      if (storedRunId) {
        setActiveRunId(storedRunId);
      } else {
        setActiveRunId(null);
      }
    }
  }, [thread?._id]);
  
  // Simplified refs for event logging only
  const eventLogRef = useRef([]);
  const bottomRef = useRef(null);
  const prevThreadIdRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const activeRunIdRef = useRef(null); // Use ref to reliably track run_id across renders

  // Create a Client instance with credentials support
  const client = useMemo(() => {
    const apiUrl = process.env.REACT_APP_ENVIRON === 'development' 
      ? apisConfig.dev 
      : apisConfig.prod;
    
    return new Client({
      apiUrl: `${apiUrl}/api/langgraph`,
      onRequest: (url, init) => {  
        const headers = {
          ...init.headers,
          'x-organization-id': orgId,
          'x-thread-id': threadId || '',
          'x-user-id': userInfo?._id || '',
          'x-assistant-id': 'ga4_v2'
        };
        
        return {
          ...init,
          headers,
          credentials: 'include' // Ensures cookies are sent with all requests
        };
      }
    });
  }, [orgId, threadId, userInfo?._id]);

  // Handle reconnection when connection errors occur - defined before useStream to avoid stale closures
  const handleReconnection = useCallback((error) => {
    if (reconnectAttempts >= 3) {
      setError('Failed to reconnect. Please refresh the page.');
      setIsReconnecting(false);
      return;
    }

    setIsReconnecting(true);
    setReconnectAttempts(prev => prev + 1);

    // Clear any existing timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Exponential backoff: 2s, 4s, 8s
    const backoffDelay = Math.pow(2, reconnectAttempts) * 1000;
    
    reconnectTimeoutRef.current = setTimeout(() => {
      // The threadStream hook will automatically retry if threadId is still set
      setIsReconnecting(false);
    }, backoffDelay);
  }, [reconnectAttempts]);

  // Initialize thread with useStream
  // threadId (UUID from server) is passed on FIRST invocation
  // LangGraph will create the thread with this ID using if_not_exists: "create"
  // reconnectOnMount: enables automatic rejoin of active streams when component remounts
  // fetchStateHistory: loads prior messages automatically on reconnect
  
  // Debug: Log all callbacks
  
  const threadStream = useStream({
    client: client,
    assistantId: "ga4_v2",
    // assistantId: "ga4_v1", // test bnew version od agent with modular architecture 
    threadId: threadId, // UUID from server - used for LangGraph thread identification
    reconnectOnMount: true, // ✅ Auto-rejoin active streams when component remounts
    fetchStateHistory: true, // ✅ Load message history automatically

    onMetadataEvent: (evt) => {
      eventLogRef.current.push({ type: 'metadata', event: evt, timestamp: new Date() });
      
      // Capture the run_id from metadata event
      if (!activeRunIdRef.current && evt?.run_id) {
        activeRunIdRef.current = evt.run_id;
        setActiveRunId(evt.run_id);
        if (threadId) {
          sessionStorage.setItem(`ga4_run_${threadId}`, evt.run_id);
        }
      }
    },

    onLangChainEvent: (evt) => {
      eventLogRef.current.push({ type: 'langchain', event: evt, timestamp: new Date() });

      const nodeName = evt?.metadata?.langgraph_node || evt?.name || "unknown";

      if (evt.event === "on_chain_start") {
        setCurrentNode(nodeName);
      }
    },

    onUpdateEvent: (data, options) => {
      eventLogRef.current.push({ type: 'update', event: data, timestamp: new Date() });
    },

    onFinish: () => {
      setCurrentNode(null);
      setIsRunning(false);
      setIsReconnecting(false);
      setReconnectAttempts(0);
      if (threadId) {
        sessionStorage.removeItem(`ga4_run_${threadId}`);
      }
      setActiveRunId(null);
      activeRunIdRef.current = null;
    },

    onError: (error) => {
      // Check if this is a fatal error (thread not found or server error)
      const isFatalError = error?.message?.includes('404') || error?.message?.includes('Thread not found');
      
      if (isFatalError) {
        setError('Thread not found. It may have been deleted due to data retention policies.');
        setIsReconnecting(false);
        setReconnectAttempts(0);
      } else {
        // For transient connection errors, attempt reconnection
        handleReconnection(error);
      }
      
      setCurrentNode(null);
      setIsRunning(false);
      setIsInitializing(false);
      activeRunIdRef.current = null;
      setActiveRunId(null);
    }, 
    onCustomEvent: (event, options) => {
      options.mutate((prev) => {
        const ui = uiMessageReducer(prev.ui ?? [], event);
        return { ...prev, ui };
      });
    }
  });

  // Group messages for proper display - moved after thread initialization
  const messages = useMemo(() => {
    const currentThreadIdentifier = thread?._id;
    const prevThreadIdentifier = prevThreadIdRef.current;
    
    // If thread changed, return empty array momentarily for smooth transition
    if (currentThreadIdentifier !== prevThreadIdentifier && prevThreadIdentifier !== null) {
      return [];
    }
    
    const msgs = (threadStream?.messages || []).filter(m => m.type === "human" || m.type === "ai");
    
    // Turn off initializing state once we have messages
    if (msgs.length > 0 && isInitializing) {
      setIsInitializing(false);
    }
    
    return msgs;
  }, [threadStream?.messages, thread?._id, isInitializing]);
  
  const grouped = useMemo(() => groupMessages(messages), [messages]);

  // Track thread changes
  useEffect(() => {
    const currentThreadIdentifier = thread?._id;
    prevThreadIdRef.current = currentThreadIdentifier;
  }, [thread?._id]);

  // Load thread history when threadId changes
  // NOTE: Commented out - causing 401 errors and not currently used
  // The useStream hook handles history internally via fetchStateHistory option
  /*
  useEffect(() => {
    
    if (threadId) {
      const client = new Client({ 
        apiUrl: "http://localhost:3001/api/langgraph",
        callerOptions: {
          credentials: 'include' // Ensure cookies are sent
        }
      });
      client.threads.getHistory(threadId, { limit: 50 })
        .then(history => {
          setThreadHistory(history);
        })
        .catch(err => {
          // Silently handle - thread may not exist yet
        });
    }
  }, [threadId]);
  */

  // Auto-scroll to bottom when messages change
  useEffect(() => { 
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); 
  }, [messages.length, threadStream?.isLoading]);

  // Rejoin stream if we return to a thread with an active run
  useEffect(() => {
    if (activeRunId && threadId && threadStream?.joinStream) {
      setIsReconnecting(true);
      
      threadStream.joinStream(activeRunId)
        .then(() => {
          setIsReconnecting(false);
          // Clear the stored run_id once we've rejoined
          sessionStorage.removeItem(`ga4_run_${threadId}`);
          setActiveRunId(null);
        })
        .catch((error) => {
          setIsReconnecting(false);
          // If rejoin fails, clear the stored run_id
          sessionStorage.removeItem(`ga4_run_${threadId}`);
          setActiveRunId(null);
        });
      
      // Cleanup: ensure we only attempt rejoin once per activeRunId
      return () => {
        // No cleanup needed - the promise will complete
      };
    }
  }, [activeRunId, threadId]); // Removed threadStream?.joinStream to prevent infinite loop

  // Handle visibility change (user switches tabs/windows)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User left the tab, stream will pause
      } else {
        // User returned to tab, stream will resume automatically
        if (threadId && threadStream?.isLoading) {
          setIsReconnecting(false);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [threadId, threadStream?.isLoading]);

  // Cleanup reconnect timeout on component unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      // Clean up stored run_id if component unmounts (thread deleted or navigated away permanently)
      if (threadId) {
        sessionStorage.removeItem(`ga4_run_${threadId}`);
      }
    };
  }, [threadId]);

  // Run the agent with payload
  const handleRunAgent = () => {
    if (!thread?.prompt) {
      return;
    }

    setError(null); // Clear any previous errors
    setIsRunning(true);
    setReconnectAttempts(0);
    setIsReconnecting(false);

    // Build client payload from thread data (no apiKey)
    const clientPayload = {
      prompt: thread.prompt,
      agentType: thread.agentType,
      granularity: thread.granularity,
      periodType: thread.periodType,
      dateRanges: thread.dateRanges,
      config: thread.config,
      orgId: orgId,
      userId: userInfo?._id
    };

    threadStream.submit(
      { 
        messages: [{ type: "human", content: thread.prompt }]
        // , client_payload: clientPayload // langgraph cloude now fetches the payload from mongodb 
      },
      { 
        streamMode: ["messages", "updates"],
        streamSubgraphs: true,   
        threadId: threadId ,
        ifNotExists: "create"  // ← ADD THIS (camelCase in JS)
      }
    );
  };

  const handleStop = async () => {
    await threadStream.stop();
    
    const runIdToCancel = activeRunIdRef.current;
    
    if (threadId && runIdToCancel) {
      try {
        await threadStream.client.runs.cancel(threadId, runIdToCancel, false, "interrupt");
        
        sessionStorage.removeItem(`ga4_run_${threadId}`);
        setActiveRunId(null);
        activeRunIdRef.current = null;
      } catch (error) {
        // Handle cancellation errors silently
      }
    }
    
    setCurrentNode(null);
    setIsRunning(false);
  };

  // Handle follow-up message submission
  const handleSubmit = (message) => {
    if (!message || !threadId) {
      return;
    }
    
    setIsRunning(true);
    
    // Submit the new message to the existing thread
    threadStream.submit(
      { 
        messages: [{ type: "human", content: message }]
      },
      { 
        streamMode: ["messages", "updates"],
        streamSubgraphs: true,   
        threadId: threadId
      }
    );
  };

  // Auto-run agent when shouldRunAgent is set
  useEffect(() => {
    if (shouldRunAgent && thread && shouldRunAgent._id === thread._id && threadId) {
      setIsInitializing(true); // Set initializing immediately
      handleRunAgent();
      if (onAgentRun) onAgentRun(); // Clear the trigger
    }
  }, [shouldRunAgent, thread, threadId]);

  return (
    <div className="flex h-full">
      <div className="flex-1 flex flex-col">
        
        {/* Scheduled Run Header - Show for scheduled threads */}
        {thread?.scheduled && !isInitializing && (
          <ScheduledRunHeader thread={thread} />
        )}

        {/* Reconnection indicator */}
        {isReconnecting && (
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse"></div>
            <span className="text-sm text-blue-700">
              Reconnecting to stream... (Attempt {reconnectAttempts}/3)
            </span>
          </div>
        )}
        
        {/* Fullscreen loader while initializing or waiting for messages */}
        {isInitializing && (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-pink"></div>
          </div>
        )}
        
        {/* Main Content */}
        {!isInitializing && (
        <div className="flex-1 overflow-y-auto" style={{ overflowX: 'visible' }}>
          <div className="mx-auto max-w-6xl p-3 sm:p-4" style={{ overflowX: 'visible' }}>
            <div className="flex flex-col gap-3">
              
              {/* Error message */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                  {error}
                </div>
              )}
              
              {/* Show thread content when no threadId exists and not running */}
              {!threadId && thread && !isRunning && (
                <>
                  <div className="rounded-xl border border-border bg-background/80 backdrop-blur-sm p-6 shadow-sm">
                    <div className="mb-4 pb-4 border-b border-border">
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Analysis Configuration
                      </h3>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 overflow-auto">
                      <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {JSON.stringify({
                          prompt: thread.prompt,
                          agentType: thread.agentType,
                          granularity: thread.granularity,
                          periodType: thread.periodType,
                          dateRanges: thread.dateRanges,
                          config: thread.config
                        }, null, 2)}
                      </pre>
                    </div>
                  </div>

                  {/* Run Button - Centered and Styled */}
                  {!isRunning && (
                    <div className="flex justify-center py-6">
                      <button
                        onClick={handleRunAgent}
                        disabled={!thread?.prompt}
                        className="group relative flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-brand-pink via-brand-pink to-brand-pink hover:from-brand-pink/90 hover:via-brand-pink/90 hover:to-brand-pink/90 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-2xl font-semibold text-sm tracking-wide transition-all duration-300 shadow-xl hover:shadow-brand-pink/25 transform hover:-translate-y-0.5 hover:scale-105 disabled:transform-none disabled:scale-100"
                        style={{ 
                          fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
                          letterSpacing: '0.025em'
                        }}
                      >
                        {/* Enhanced Icon with Animation */}
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white/20 group-hover:bg-white/30 transition-all duration-300 group-hover:rotate-12 group-hover:scale-110">
                          <svg 
                            className="w-4 h-4 transition-all duration-300 group-hover:scale-110" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              d="M13 10V3L4 14h7v7l9-11h-7z" 
                            />
                          </svg>
                        </div>
                        
                        {/* Enhanced Text */}
                        <span className="relative">
                          Run Analysis
                          {/* Text shimmer effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-700 ease-in-out"></div>
                        </span>
                        
                        {/* Enhanced glow effect */}
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-brand-pink/40 via-brand-pink/30 to-brand-pink/40 blur-xl opacity-0 group-hover:opacity-40 transition-all duration-300 -z-10 scale-105"></div>
                        
                        {/* Border highlight */}
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-white/5 via-white/10 to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Show conversation when threadId exists */}
              {threadId && grouped.map((group, gi) => (
                <MessageGroup key={gi} side={group.type === "human" ? "right" : "left"} type={group.type}>
                  {/* Show stepper if AI group has multiple items OR if we're currently loading and this is the last AI group */}
                  {group.type === "ai" && (
                    group.items.length > 1 || 
                    (threadStream?.isLoading && currentNode && gi === grouped.length - 1)
                  ) ? (
                    <AiVerticalStepper 
                      items={group.items} 
                      thread={threadStream} 
                      currentNode={currentNode}
                      isLoading={threadStream?.isLoading}
                    />
                  ) : (
                    group.items.map((m) => (
                      <div key={m.id}>
                        <MessageBubble type={group.type}>
                          <SmartContentRenderer content={m.content} />
                          <TimeStamp value={m.created_at} align={group.type === "human" ? "right" : "left"} />
                        </MessageBubble>

                        {/* Render UI Components associated with this message */}
                        {threadStream?.values?.ui
                          ?.filter((ui) => ui.metadata?.message_id === m.id)
                          .map((ui) => (
                            <div 
                              key={ui.id} 
                              className="mt-3 w-full chart-container" 
                              style={{ 
                                maxWidth: '100%',
                                position: 'relative',
                                boxSizing: 'border-box',
                                // Add iframe-like isolation for chart components
                                contain: 'layout style paint',
                                isolation: 'isolate',
                                overflow: 'hidden',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb'
                              }}
                            >
                              {/* Simple Preview/Toggle Solution */}
                              <div className="bg-white p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-sm font-medium text-gray-900">📊 Interactive Chart</h4>
                                  <button
                                    onClick={() => {
                                      // Use your existing chart-viewer.html
                                      const chartData = { message: ui, stream: threadStream };
                                      const jsonString = JSON.stringify(chartData);
                                      const chartId = `chart_${Date.now()}`;
                                      localStorage.setItem(chartId, jsonString);
                                      window.open(`/chart-viewer.html?chartId=${chartId}`, '_blank');
                                    }}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg transition-colors"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    Open Perfect Chart
                                  </button>
                                </div>
                                
                                {/* Embedded chart with better isolation */}
                                <div style={{ 
                                  minHeight: '300px',
                                  contain: 'layout style',
                                  isolation: 'isolate'
                                }}>
                                  <SafeLoadExternalComponent
                                    stream={threadStream}
                                    message={ui}
                                    fallback={
                                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-center space-x-2">
                                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                          <span className="text-gray-600">Loading GA4 component...</span>
                                        </div>
                                      </div>
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ))
                  )}
                </MessageGroup>
              ))}

              {/* Show a new stepper if we're loading and the last message is from human (waiting for AI response) */}
              {threadId && threadStream?.isLoading && currentNode && grouped.length > 0 && grouped[grouped.length - 1].type === "human" && (
                <MessageGroup side="left" type="ai">
                  <AiVerticalStepper 
                    items={[]} 
                    thread={threadStream} 
                    currentNode={currentNode}
                    isLoading={threadStream?.isLoading}
                  />
                </MessageGroup>
              )}

              {/* Only show typing indicator if we're loading but NOT in a grouped AI conversation */}
              {/* {threadId && thread?.isLoading && currentNode && !grouped.some(g => g.type === "ai" && (g.items.length > 1 || thread?.isLoading)) && (
                <TypingIndicator text={`Processing: ${createStepInfo(currentNode)?.title}`} />
              )} */}
              <div ref={bottomRef} />
              
              {/* Show Input Box when thread exists, NOT running, and has messages */}
              {threadId && !isRunning && grouped.length > 0 && (
                <div className="mt-6">
                  <FloatingChatInput
                    onSubmit={handleSubmit}
                    onStop={handleStop}
                    isLoading={isRunning}
                    disabled={!threadId}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Stop Button - Floating on bottom right when agent is running */}
        {threadId && isRunning && (
          <button
            onClick={handleStop}
            className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 font-sans z-50"
            title="Stop processing"
          >
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span className="font-medium text-sm">Stop</span>
          </button>
        )}

      </div>
    </div>
  );
};

export default GA4Agent;